import styled, { css } from 'styled-components';
import { globalTheme } from '@clds/component-theme';
import { typography } from '@clds/typography';
import { media } from '@clds/style-utils';
import { theme as t } from '../ColumnPanelTheme';
import { Root as CheckboxRecord } from '../CheckboxRecord/CheckboxRecord';
import { StyledLink } from './useLimitedChildren';

const { spacing } = globalTheme;

export interface RootProps {
  recordCount: number;
  recordsPerColumn: number;
}

export const SectionTitle = styled.header`
  ${typography.xs.highContrast};
  margin-bottom: ${globalTheme.spacing.sm};
`;

const getColumnLayout = ({ recordCount, recordsPerColumn, columnWidth }: ColumnWrapperProps) => css`
  columns: ${() => Math.ceil(recordCount / recordsPerColumn)} auto;
  column-gap: ${spacing.md};
  column-fill: auto;

  ${CheckboxRecord} {
    margin-bottom: ${spacing.lg};
    min-width: ${columnWidth === 'auto' ? 'auto' : `${columnWidth}px`};

    :nth-child(${recordsPerColumn}n + 1),
    :first-of-type {
      padding-top: ${t.panelSection.paddingFix}px;
    }
    :last-child {
      margin: 0;
    }
  }
`;

const getFlexLayout = ({ recordCount }: RootProps) => css`
  display: flex;
  flex-wrap: wrap;

  ${SectionTitle} {
    width: 100%;
  }

  ${CheckboxRecord} {
    width: 50%;
    margin: 0 0 ${spacing.lg} 0;
    /* Necessary to prevent stacking padding + margin on mobile */
    :nth-last-child(1) ${() => (recordCount % 2 === 0 ? ',:nth-last-child(2)' : '')} {
      margin: 0;
    }
  }

  ${StyledLink} {
    margin: 0;
  }
`;

export const Root = styled.div<RootProps>`
  padding: ${spacing.lg} ${spacing.xl} 0;
  @media ${media.phone()} {
    padding: ${spacing.xl};
  }

  ${CheckboxRecord}, ${StyledLink} {
    @media ${media.phone()} {
      ${({ recordsPerColumn }) => css`
        margin: ${spacing.lg} 0;
        align-self: start;
        :nth-child(${recordsPerColumn}n + 2),
        :first-of-type {
          margin-top: 0;
        }
      `};
    }
  }
`;

export interface ColumnWrapperProps extends RootProps {
  columnWidth: 'auto' | number;
}

export const ColumnWrapper = styled.div<ColumnWrapperProps>`
  @media ${media.tablet({ minOnly: true })} {
    ${(props) => getColumnLayout(props)};
  }
  @media ${media.phone()} {
    ${(props) => getFlexLayout(props)}
  }
`;
